import isEmpty from 'lodash/isEmpty';
import ToggleFilter from '../ToggleFilter';
import DateFilter from '../DateFilter';
import noop from '../../../util/noop';
import moment from 'moment';
import i18n from '../../../i18n/translate';

/**
 * Converts a filter array to a key/value object for Foundry API filtering
 * @param {Array} filtersArray An array of filters
 * @param {string} keyField The key field (default is "name")
 * @param {string} valueField  The value field (default is "value")
 */
export const filtersArrayToKeyValueObject = (filtersArray, keyField = 'name', valueField = 'value') => {
  return filtersArray.reduce((acc, filter) => {
    acc[filter[keyField]] = filter[valueField];
    return acc;
  }, {});
}

/**
 * Get filter collection to render as category chips
 * @param {Array} filters An array of filters
 */
export const getFilterChips = filters => {
  return filters.reduce((chips, filter) => {
    if (!filter.value) {
      return chips;
    }

    if (filter.isTwoStep) {
      filter.value.forEach(value => {
        value.values.forEach(val => chips.push({ ...filter, ...value, value: val }));
      });
    } else if (Array.isArray(filter.value)) {
      filter.value.forEach(value => {
        chips.push({ ...filter, value });
      });
    } else if (typeof filter.value === 'boolean') {
      chips.push(filter);
    } else {
      // custom object filters
      const values = Object.values(filter.value);

      if (values.length && values.some(val => !!val)) {
        if(filter.type === DateFilter) {
          formatDateFilterChip(filter);
        }

        chips.push(filter);
      }
    }

    return chips;
  }, []);
}

/**
 * Returns true if the filter is active, or false if it's not, based on the value
 * @param {Object} filter A filter object
 */
export const isFilterActive = filter => {
  const { value } = filter;

  let isActive = false;
  if (filter.isTwoStep) {
    isActive = value.flatMap(val => val.values).length > 0;
  } else if (Array.isArray(value)) {
    isActive = !!value.length && value.some(val => !isEmpty(val));
  } else if (typeof value === 'boolean') {
    isActive = value;
  } else if (value) {
    isActive = !!Object.keys(value).length;
  }

  return isActive;
}

const getInitialFilterValue = (filter, valueKeys) => {
  const { options, valueKey } = filter;

  if (typeof valueKeys === 'boolean') {
    return valueKeys ? { id: valueKeys, label: filter.checkedLabel || `Filtering By ${filter.label}` } : false;
  }

  const hasOptionsAvailable = Array.isArray(valueKeys) && options;
  const valueField = valueKey || 'id';

  return hasOptionsAvailable ? valueKeys.map(val => val[valueField] ? val : options.find(opt => opt[valueField] === val)).filter(val => val) : valueKeys;
}

/**
 * Get initial filters based on configuration and initial
 * @param {Array} config An array of filters
 * @param {Object} initial An object containing the initial filters
 */
export const getInitialFilters = (config, initial) => {
  return config.map(filter => {
    const valueKeys = initial[filter.name];

    let value = [], defaultValue = [];
    if (valueKeys) {
      value = getInitialFilterValue(filter, valueKeys);
      defaultValue = valueKeys;
    }

    if (filter.type === ToggleFilter) {
      value = Array.isArray(value) && !value?.length ? false : value;
    }

    return Object.assign({}, filter, { value, defaultValue });
  });
}

/**
 * Receives a collection of filters and returns those same filters with cleared values
 * @param {Array} collection An array of filters
 * @param {Function} clearToggleFilter Function to clear toggle filter specifically (optional)
 */
export const clearAllFilters = (collection, clearToggleFilter = noop) => {
  return collection.map(filter => {
    if (Array.isArray(filter.value)) {
      filter.value = [];
    } else {
      filter.value = {};

      if (filter.type === ToggleFilter){
        filter = { ...filter, value: false };
      }
    }

    return filter;
  });
}

/**
 * Receives a collection of filters and returns the same collection with a specific filter value cleared
 * @param {Object} filterToRemove The filter to be removed (have it's value cleared out)
 * @param {Array} collection An array of filters
 * @param {Function} clearToggleFilter Function to clear toggle filter specifically (optional)
 */
export const removeFilterFromCollection = (filterToRemove, collection, clearToggleFilter = noop) => {
  return collection.map(filter => {
    if (filter.name === filterToRemove.name) {
      if (filter.isTwoStep) {
        const nestedFilter = filter.value.find(val => val.id === filterToRemove.id)
        const otherFilters = filter.value.filter(val => val.id !== filterToRemove.id)
        const nestedValues = nestedFilter.values.filter(val => val.id !== filterToRemove.value.id);
        filter = { ...filter, value: [ ...otherFilters, { ...nestedFilter, values: nestedValues } ] };
      } else if (Array.isArray(filter.value)) {
        filter = { ...filter, value: filter.value.filter(value => value !== filterToRemove.value) };
      } else {
        filter = { ...filter, value: {} };

        if (filter.type === ToggleFilter){
          filter = { ...filter, value: false };
        }
      }
    }

    return filter;
  });
}

/**
 * Returns a filter unique key
 * @param {Object} filter A filter object
 */
export const getFilterUniqueKey = (filter) => {
  if (typeof filter?.value === "string") {
    return `chip_${filter.name}_${filter.value}`;
  } else if (typeof filter?.value === "object") {
    return `chip_${filter.name}_${filter.value.id || filter.value.value}`;
  }
}

/**
 * Returns formatted strings for date filter chip
 * @param {Object} filter A filter object
 */
export const formatDateFilterChip = filter => {
  const { from_date, to_date } = filter.value;

  if (from_date && to_date) {
    return i18n.t('Between {{ from_date }} and {{ to_date }}', { from_date: moment(from_date).format('MM/DD/YYYY'), to_date: moment(to_date).format('MM/DD/YYYY') })
  } else if (from_date) {
    return i18n.t('After {{ from_date }}', { from_date: moment(from_date).format('MM/DD/YYYY') })
  } else if (to_date) {
    return i18n.t('Before {{ to_date }}', { to_date: moment(to_date).format('MM/DD/YYYY') })
  }
}
